import type { Projectile, ProjectileSource, TrailParticle } from "@/types/projectile.types"
import type { Position } from "@/types/game.types"
import type { Enemy } from "@/types/enemy.types"
import type { Hero, AttackType } from "@/types/hero.types"
import type { Tower } from "@/types/tower.types"
import type { SummonedUnit } from "@/types/summon.types"
import { generateId } from "@/utils/id-generator"
import { distanceSquared } from "@/utils/collision"
import { gameSettings } from "@/data/game-settings"
import { ObjectPool } from "@/utils/object-pool"
import type { SpatialGrid } from "@/utils/spatial-grid"
import { getNearbyEntities } from "@/utils/spatial-grid"
import { gameNow } from "@/utils/game-clock"

// Projectile object pool for reducing GC pressure
const projectilePool = new ObjectPool<Projectile>(
  () => ({
    id: "",
    position: { x: 0, y: 0 },
    velocity: { x: 0, y: 0 },
    damage: 0,
    speed: 0,
    targetId: "",
    source: "tower" as ProjectileSource,
    sourceId: "",
    lifetime: 0,
    color: "",
    size: 0,
    isCritical: false,
  }),
  (proj) => {
    proj.id = ""
    proj.position.x = 0
    proj.position.y = 0
    proj.velocity.x = 0
    proj.velocity.y = 0
    proj.damage = 0
    proj.speed = 0
    proj.targetId = ""
    proj.source = "tower"
    proj.sourceId = ""
    proj.lifetime = 0
    proj.color = ""
    proj.size = 0
    proj.isCritical = false
    proj.heroType = undefined
    proj.towerType = undefined
    proj.attackType = undefined
    proj.isArc = false
    proj.arcStartPos = undefined
    proj.arcTargetPos = undefined
    proj.arcProgress = undefined
    proj.arcHeight = undefined
    proj.arcDuration = undefined
    proj.trailParticles = undefined
    proj.hasBounced = undefined
  },
  50, // Pre-allocate 50 projectiles
  200 // Max pool size
)

export const createProjectile = (
  from: Position,
  to: Position,
  damage: number,
  speed: number,
  targetId: string,
  source: ProjectileSource,
  sourceId: string,
  isCritical: boolean = false,
): Projectile => {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const dist = Math.sqrt(dx * dx + dy * dy)

  const velocity = {
    x: (dx / dist) * speed,
    y: (dy / dist) * speed,
  }

  let color = gameSettings.projectile.towerColor
  if (source === "enemy") {
    color = gameSettings.projectile.enemyColor
  } else if (source === "hero") {
    // Critical hits use a golden/yellow color
    color = isCritical ? "#FFD700" : gameSettings.projectile.heroColor
  }

  return {
    id: generateId("projectile"),
    position: { ...from },
    velocity,
    damage,
    speed,
    targetId,
    source,
    sourceId,
    lifetime: gameSettings.projectile.defaultLifetime,
    color,
    size: isCritical ? gameSettings.projectile.defaultSize * 1.5 : gameSettings.projectile.defaultSize,
    isCritical,
  }
}

/**
 * Create a projectile using the object pool (more efficient, reduces GC)
 */
export const createProjectilePooled = (
  from: Position,
  to: Position,
  damage: number,
  speed: number,
  targetId: string,
  source: ProjectileSource,
  sourceId: string,
  isCritical: boolean = false,
  heroType?: string,
  towerType?: string,
  attackType?: AttackType,
  customColor?: string,
): Projectile => {
  const proj = projectilePool.acquire()

  const dx = to.x - from.x
  const dy = to.y - from.y
  const dist = Math.sqrt(dx * dx + dy * dy)

  proj.id = generateId("projectile")
  proj.position.x = from.x
  proj.position.y = from.y
  proj.velocity.x = (dx / dist) * speed
  proj.velocity.y = (dy / dist) * speed
  proj.damage = damage
  proj.speed = speed
  proj.targetId = targetId
  proj.source = source
  proj.sourceId = sourceId
  proj.lifetime = gameSettings.projectile.defaultLifetime
  proj.isCritical = isCritical
  proj.size = isCritical ? gameSettings.projectile.defaultSize * 1.5 : gameSettings.projectile.defaultSize
  proj.heroType = heroType
  proj.towerType = towerType
  proj.attackType = attackType

  // Clear arc fields for non-arc projectiles
  proj.isArc = false
  proj.arcStartPos = undefined
  proj.arcTargetPos = undefined
  proj.arcProgress = undefined
  proj.arcHeight = undefined
  proj.arcDuration = undefined

  // Mage projectile scaling fields
  if (towerType === "mage") {
    proj.spawnTime = gameNow()
    proj.initialSpeed = speed
    proj.initialSize = proj.size
    proj.initialAoeRadius = 30 // Base AoE radius for mage towers
  } else {
    proj.spawnTime = undefined
    proj.initialSpeed = undefined
    proj.initialSize = undefined
    proj.initialAoeRadius = undefined
  }

  if (source === "enemy") {
    proj.color = customColor ?? gameSettings.projectile.enemyColor
  } else if (source === "hero") {
    proj.color = isCritical ? "#FFD700" : gameSettings.projectile.heroColor
  } else {
    proj.color = gameSettings.projectile.towerColor
  }

  return proj
}

/**
 * Create an arc projectile for archer towers (arrow that flies in an arc)
 */
export const createArcProjectile = (
  from: Position,
  to: Position,
  damage: number,
  targetId: string,
  sourceId: string,
  arcHeight: number = 40,
  arcDuration: number = 500,
  attackType?: AttackType,
): Projectile => {
  const proj = projectilePool.acquire()

  proj.id = generateId("projectile")
  proj.position.x = from.x
  proj.position.y = from.y
  // Velocity is calculated per-frame for arc projectiles, but we store direction
  const dx = to.x - from.x
  const dy = to.y - from.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  proj.velocity.x = dx / dist // Normalized direction for rotation calculation
  proj.velocity.y = dy / dist
  proj.damage = damage
  proj.speed = 0 // Not used for arc projectiles
  proj.targetId = targetId
  proj.source = "tower"
  proj.sourceId = sourceId
  proj.lifetime = arcDuration * 2 // Give it extra time, will be marked expired when arc completes
  proj.isCritical = false
  proj.size = gameSettings.projectile.defaultSize
  proj.color = gameSettings.projectile.towerColor
  proj.towerType = "archer"
  proj.attackType = attackType

  // Arc trajectory fields
  proj.isArc = true
  proj.arcStartPos = { x: from.x, y: from.y }
  proj.arcTargetPos = { x: to.x, y: to.y }
  proj.arcProgress = 0
  proj.arcHeight = arcHeight
  proj.arcDuration = arcDuration

  return proj
}

/**
 * Create an arc projectile for ranger hero (arrow that flies in an arc like archer tower)
 */
export const createRangerArcProjectile = (
  from: Position,
  to: Position,
  damage: number,
  targetId: string,
  sourceId: string,
  isCritical: boolean = false,
  arcHeight: number = 40,
  arcDuration: number = 500,
): Projectile => {
  const proj = projectilePool.acquire()

  proj.id = generateId("projectile")
  proj.position.x = from.x
  proj.position.y = from.y
  // Velocity is calculated per-frame for arc projectiles, but we store direction
  const dx = to.x - from.x
  const dy = to.y - from.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  proj.velocity.x = dx / dist // Normalized direction for rotation calculation
  proj.velocity.y = dy / dist
  proj.damage = damage
  proj.speed = 0 // Not used for arc projectiles
  proj.targetId = targetId
  proj.source = "hero"
  proj.sourceId = sourceId
  proj.lifetime = arcDuration * 2 // Give it extra time, will be marked expired when arc completes
  proj.isCritical = isCritical
  proj.size = isCritical ? gameSettings.projectile.defaultSize * 1.5 : gameSettings.projectile.defaultSize
  proj.color = isCritical ? "#FFD700" : gameSettings.projectile.heroColor
  proj.heroType = "ranger"
  proj.towerType = "archer" // Use archer rendering for arrow sprites

  // Arc trajectory fields
  proj.isArc = true
  proj.arcStartPos = { x: from.x, y: from.y }
  proj.arcTargetPos = { x: to.x, y: to.y }
  proj.arcProgress = 0
  proj.arcHeight = arcHeight
  proj.arcDuration = arcDuration

  return proj
}

/**
 * Create an arc projectile for siege towers (boulder that flies in an arc, no auto-hit)
 * Similar to archer projectile but with different visual settings
 */
export const createSiegeArcProjectile = (
  from: Position,
  to: Position,
  damage: number,
  targetId: string,
  sourceId: string,
  arcHeight: number = 60,
  arcDuration: number = 800,
  attackType?: AttackType,
): Projectile => {
  const proj = projectilePool.acquire()

  proj.id = generateId("projectile")
  proj.position.x = from.x
  proj.position.y = from.y
  // Velocity is calculated per-frame for arc projectiles, but we store direction
  const dx = to.x - from.x
  const dy = to.y - from.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  proj.velocity.x = dx / dist // Normalized direction
  proj.velocity.y = dy / dist
  proj.damage = damage
  proj.speed = 0 // Not used for arc projectiles
  proj.targetId = targetId
  proj.source = "tower"
  proj.sourceId = sourceId
  proj.lifetime = arcDuration * 2 // Give it extra time, will be marked expired when arc completes
  proj.isCritical = false
  proj.size = gameSettings.projectile.defaultSize
  proj.color = "#8B4513" // Brown for boulder
  proj.towerType = "siege"
  proj.attackType = attackType

  // Arc trajectory fields
  proj.isArc = true
  proj.arcStartPos = { x: from.x, y: from.y }
  proj.arcTargetPos = { x: to.x, y: to.y }
  proj.arcProgress = 0
  proj.arcHeight = arcHeight
  proj.arcDuration = arcDuration

  // Initialize trail particles array
  proj.trailParticles = []

  return proj
}

/**
 * Update siege projectile trail particles
 * Called during projectile update to spawn and update smoke particles
 */
export const updateSiegeProjectileTrail = (projectile: Projectile, deltaMs: number): void => {
  if (projectile.towerType !== "siege") return

  // Initialize trail array if needed
  if (!projectile.trailParticles) projectile.trailParticles = []

  // Spawn new smoke particle every ~50ms
  if (Math.random() < deltaMs / 50) {
    const particle: TrailParticle = {
      x: projectile.position.x + (Math.random() - 0.5) * 4,
      y: projectile.position.y + (Math.random() - 0.5) * 4,
      size: 3 + Math.random() * 3,
      life: 400,
      maxLife: 400,
    }
    projectile.trailParticles.push(particle)
  }

  // Update existing particles (fade out and float up)
  for (let i = projectile.trailParticles.length - 1; i >= 0; i--) {
    const p = projectile.trailParticles[i]
    p.life -= deltaMs
    p.y -= 0.3 * (deltaMs / 16.67) // Float up

    // Remove dead particles
    if (p.life <= 0) {
      projectile.trailParticles.splice(i, 1)
    }
  }
}

/**
 * Release a projectile back to the pool
 */
export const releaseProjectile = (projectile: Projectile): void => {
  projectilePool.release(projectile)
}

/**
 * Update projectile position in-place (more efficient)
 */
export const updateProjectileInPlace = (projectile: Projectile, delta: number): void => {
  // Mage projectile scaling: speed up and grow over time
  if (projectile.towerType === "mage" && projectile.spawnTime && projectile.initialSpeed && projectile.initialSize) {
    const elapsed = gameNow() - projectile.spawnTime
    const maxScaleTime = 2000 // Max scaling over 2 seconds
    const progress = Math.min(elapsed / maxScaleTime, 1)

    // Speed increases by 100% over time (1x to 2x)
    const speedMultiplier = 1 + progress
    const currentSpeed = projectile.initialSpeed * speedMultiplier

    // Normalize velocity to new speed
    const vx = projectile.velocity.x
    const vy = projectile.velocity.y
    const currentVelocityMag = Math.sqrt(vx * vx + vy * vy)
    if (currentVelocityMag > 0) {
      projectile.velocity.x = (vx / currentVelocityMag) * currentSpeed
      projectile.velocity.y = (vy / currentVelocityMag) * currentSpeed
    }
    projectile.speed = currentSpeed

    // Size increases by 150% over time (1x to 2.5x)
    projectile.size = projectile.initialSize * (1 + progress * 1.5)
  }

  projectile.position.x += projectile.velocity.x * delta
  projectile.position.y += projectile.velocity.y * delta
  projectile.lifetime -= delta
}

/**
 * Ease-in-out cubic: smooth start and end
 */
const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

/**
 * Ease-in cubic: starts slow, ends fast
 */
const easeInCubic = (t: number): number => {
  return t * t * t
}

/**
 * Update arc projectile position in-place
 * Archer arrows use ease-in (starts slow, ends fast)
 * Siege boulders use ease-in-out (smooth arc)
 * @param deltaMs - Time delta in milliseconds
 */
export const updateArcProjectileInPlace = (projectile: Projectile, deltaMs: number): void => {
  if (!projectile.isArc || !projectile.arcStartPos || !projectile.arcTargetPos || projectile.arcDuration === undefined) {
    // Fall back to linear movement if arc fields are missing
    updateProjectileInPlace(projectile, deltaMs / 16.67)
    return
  }

  // Update progress (0 to 1)
  const progressDelta = deltaMs / projectile.arcDuration
  projectile.arcProgress = Math.min(1, (projectile.arcProgress || 0) + progressDelta)

  const t = projectile.arcProgress

  // Siege projectiles use ease-in-out, archer uses ease-in
  const eased = projectile.towerType === "siege" ? easeInOutCubic(t) : easeInCubic(t)

  // Linear interpolation with easing applied
  const startX = projectile.arcStartPos.x
  const startY = projectile.arcStartPos.y
  const endX = projectile.arcTargetPos.x
  const endY = projectile.arcTargetPos.y

  projectile.position.x = startX + (endX - startX) * eased
  projectile.position.y = startY + (endY - startY) * eased

  // Add arc height based on projectile type
  // Ranger arrows start horizontal and curve down: use (1 - (1-t)^2) * (1-t) which starts flat
  // Other projectiles (archer tower) use symmetric parabola that peaks at t=0.5
  let arcOffset: number
  if (projectile.heroType === "ranger") {
    // Ranger arc: starts horizontal, curves down - peaks around t=0.3
    // Uses a skewed parabola: t * (1 - t) * (2 - t) peaks earlier
    arcOffset = (projectile.arcHeight || 40) * 3.5 * t * (1 - t) * (1.5 - t)
  } else {
    // Standard symmetric parabola: peaks at t=0.5
    arcOffset = (projectile.arcHeight || 40) * 4 * t * (1 - t)
  }
  projectile.position.y -= arcOffset

  // Update velocity direction for arrow rotation (tangent to the arc)
  // Calculate instantaneous direction based on derivative of position
  const dt = 0.01
  const t2 = Math.min(1, t + dt)
  const eased2 = projectile.towerType === "siege" ? easeInOutCubic(t2) : easeInCubic(t2)
  let arcOffset2: number
  if (projectile.heroType === "ranger") {
    arcOffset2 = (projectile.arcHeight || 40) * 3.5 * t2 * (1 - t2) * (1.5 - t2)
  } else {
    arcOffset2 = (projectile.arcHeight || 40) * 4 * t2 * (1 - t2)
  }

  const nextX = startX + (endX - startX) * eased2
  const nextY = startY + (endY - startY) * eased2 - arcOffset2

  const dx = nextX - projectile.position.x
  const dy = nextY - projectile.position.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist > 0) {
    projectile.velocity.x = dx / dist
    projectile.velocity.y = dy / dist
  }

  // Mark as expired when arc is complete
  if (projectile.arcProgress >= 1) {
    projectile.lifetime = 0
  }
}

export const updateProjectile = (projectile: Projectile, delta: number): Projectile => {
  return {
    ...projectile,
    position: {
      x: projectile.position.x + projectile.velocity.x * delta,
      y: projectile.position.y + projectile.velocity.y * delta,
    },
    lifetime: projectile.lifetime - delta,
  }
}

export const isProjectileExpired = (projectile: Projectile, canvasWidth: number, canvasHeight: number): boolean => {
  if (projectile.lifetime <= 0) return true
  if (projectile.position.x < 0 || projectile.position.x > canvasWidth) return true
  if (projectile.position.y < 0 || projectile.position.y > canvasHeight) return true
  return false
}

export const checkProjectileHit = (
  projectile: Projectile,
  enemies: Enemy[],
  hero: Hero | null,
  towers: Tower[],
  summons: SummonedUnit[] = [],
): { hit: boolean; targetId?: string; targetType?: "enemy" | "hero" | "tower" | "summon" } => {
  if (projectile.source === "tower" || projectile.source === "hero") {
    for (let i = 0; i < enemies.length; i++) {
      const enemy = enemies[i]
      const hitRadius = enemy.size / 2 + projectile.size
      const hitRadiusSquared = hitRadius * hitRadius
      if (distanceSquared(projectile.position, enemy.position) <= hitRadiusSquared) {
        return { hit: true, targetId: enemy.id, targetType: "enemy" }
      }
    }
  }

  if (projectile.source === "enemy") {
    if (hero) {
      const heroHitRadius = 12 + projectile.size
      const heroHitRadiusSquared = heroHitRadius * heroHitRadius
      if (distanceSquared(projectile.position, hero.position) <= heroHitRadiusSquared) {
        return { hit: true, targetId: hero.id, targetType: "hero" }
      }
    }

    for (let i = 0; i < towers.length; i++) {
      const tower = towers[i]
      const towerHitRadius = 15 + projectile.size
      const towerHitRadiusSquared = towerHitRadius * towerHitRadius
      if (distanceSquared(projectile.position, tower.position) <= towerHitRadiusSquared) {
        return { hit: true, targetId: tower.id, targetType: "tower" }
      }
    }

    // Check summons (wolves, siege towers)
    for (let i = 0; i < summons.length; i++) {
      const summon = summons[i]
      const summonHitRadius = summon.size / 2 + projectile.size
      const summonHitRadiusSquared = summonHitRadius * summonHitRadius
      if (distanceSquared(projectile.position, summon.position) <= summonHitRadiusSquared) {
        return { hit: true, targetId: summon.id, targetType: "summon" }
      }
    }
  }

  return { hit: false }
}

// Scratch buffer for spatial-grid queries. checkProjectileHitWithGrid runs once
// per projectile per frame, so allocating a fresh result array there was pure
// GC pressure during heavy waves.
const nearbyScratch: Enemy[] = []

/**
 * Check projectile hit using spatial grid (more efficient for many enemies)
 */
export const checkProjectileHitWithGrid = (
  projectile: Projectile,
  enemyGrid: SpatialGrid<Enemy>,
  hero: Hero | null,
  towers: Tower[],
  summons: SummonedUnit[] = [],
): { hit: boolean; targetId?: string; targetType?: "enemy" | "hero" | "tower" | "summon" } => {
  if (projectile.source === "tower" || projectile.source === "hero") {
    // Only check enemies near the projectile
    const maxHitRadius = 30 // Approximate max enemy size / 2 + projectile size
    // Reuses a scratch array - the results are consumed before the next call
    const nearbyEnemies = getNearbyEntities(enemyGrid, projectile.position, maxHitRadius, nearbyScratch)

    for (let i = 0; i < nearbyEnemies.length; i++) {
      const enemy = nearbyEnemies[i]
      const hitRadius = enemy.size / 2 + projectile.size
      const hitRadiusSquared = hitRadius * hitRadius
      if (distanceSquared(projectile.position, enemy.position) <= hitRadiusSquared) {
        return { hit: true, targetId: enemy.id, targetType: "enemy" }
      }
    }
  }

  if (projectile.source === "enemy") {
    if (hero) {
      const heroHitRadius = 12 + projectile.size
      const heroHitRadiusSquared = heroHitRadius * heroHitRadius
      if (distanceSquared(projectile.position, hero.position) <= heroHitRadiusSquared) {
        return { hit: true, targetId: hero.id, targetType: "hero" }
      }
    }

    // Towers are static and few, no need for spatial grid
    for (let i = 0; i < towers.length; i++) {
      const tower = towers[i]
      const towerHitRadius = 15 + projectile.size
      const towerHitRadiusSquared = towerHitRadius * towerHitRadius
      if (distanceSquared(projectile.position, tower.position) <= towerHitRadiusSquared) {
        return { hit: true, targetId: tower.id, targetType: "tower" }
      }
    }

    // Check summons (wolves, siege towers) - few in number, no need for spatial grid
    for (let i = 0; i < summons.length; i++) {
      const summon = summons[i]
      const summonHitRadius = summon.size / 2 + projectile.size
      const summonHitRadiusSquared = summonHitRadius * summonHitRadius
      if (distanceSquared(projectile.position, summon.position) <= summonHitRadiusSquared) {
        return { hit: true, targetId: summon.id, targetType: "summon" }
      }
    }
  }

  return { hit: false }
}
